export type ApiErrorClassification =
  | "authentication"
  | "domain"
  | "expected-status"
  | "form-validation"
  | "technical"
  | "unknown";

export type ApiBackend = "api-gouv" | "backoffice" | "backoffice-v2" | "google-maps" | "kili" | "subscriber-backend";

export type ApiErrorDetails =
  | {
      backend: "backoffice" | "backoffice-v2";
      code?: string;
      message?: string;
      messageCode?: string;
    }
  | {
      backend: "kili" | "subscriber-backend";
      errorCategory: string;
      errorCode?: string;
      message?: string;
      fieldErrors?: Array<{
        propertyPath: string;
        errorCode: string;
        message: string;
      }>;
    }
  | {
      backend: "api-gouv";
    }
  | {
      backend: "google-maps";
      code?: string;
      endpoint?: string;
      message?: string;
    };

interface ApiClientErrorOptions {
  backend: ApiBackend;
  classification: ApiErrorClassification;
  request?: Request;
  response?: Response;
  status?: number;
  method?: string;
  url?: string;
  details?: ApiErrorDetails;
  originalError?: Error;
}

export class ApiClientError extends Error {
  public readonly backend: ApiBackend;
  public readonly classification: ApiErrorClassification;
  public readonly request?: Request;
  public readonly response?: Response;
  public readonly status?: number;
  public readonly method?: string;
  public readonly url?: string;
  public readonly details?: ApiErrorDetails;
  public readonly originalError?: Error;

  public constructor(options: ApiClientErrorOptions) {
    const status = options.status ? ` ${options.status}` : "";
    let code: string | undefined;
    if (options.details?.backend === "backoffice" || options.details?.backend === "backoffice-v2") {
      code = options.details.messageCode ?? options.details.code;
    } else if (options.details?.backend === "kili" || options.details?.backend === "subscriber-backend") {
      code = options.details.errorCode;
    } else if (options.details?.backend === "google-maps") {
      code = options.details.code;
    }
    const suffix = code ? ` (${code})` : "";
    super(`${options.backend} API request failed${status}${suffix}`);

    this.name = "ApiClientError";
    this.backend = options.backend;
    this.classification = options.classification;
    this.request = options.request;
    this.response = options.response;
    this.status = options.status;
    this.method = options.method;
    this.url = options.url;
    this.details = options.details;
    this.originalError = options.originalError;
  }

  public asExpectedStatus(): ApiClientError {
    return new ApiClientError({
      backend: this.backend,
      classification: "expected-status",
      request: this.request,
      response: this.response,
      status: this.status,
      method: this.method,
      url: this.url,
      details: this.details,
      originalError: this,
    });
  }
}

export function expectApiStatuses<T>(request: Promise<T>, expectedStatuses: readonly number[]): Promise<T> {
  return request.catch((error: Error) => {
    if (error instanceof ApiClientError && error.status && expectedStatuses.includes(error.status)) {
      throw error.asExpectedStatus();
    }

    throw error;
  });
}
